"use client"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@suraasa/placebo-ui"
import { useMediaQuery } from "@uidotdev/usehooks"
import clsx from "clsx"
import { ChevronDown, XIcon } from "lucide-react"
import type React from "react"
import { useEffect, useRef, useState } from "react"

interface FilterOption {
  label: string
  value: string
  isDisabled?: boolean
}

export enum Segment {
  Overall = "overall",
  Terms = "terms",
  Modules = "modules",
}

interface SegmentedFilterButtonsProps {
  onOverallClick: () => void
  termOptions: FilterOption[]
  selectedTerm: { label: string; value: string }
  onTermSelect: (option: FilterOption) => void
  onTermClear: () => void
  moduleOptions: FilterOption[]
  selectedModule: { label: string; value: string }
  onModuleSelect: (option: FilterOption) => void
  onModuleClear: () => void
  showModules?: boolean
}

export default function SegmentedFilterButtons({
  onOverallClick,
  termOptions,
  selectedTerm,
  onTermSelect,
  onTermClear,
  moduleOptions,
  selectedModule,
  onModuleSelect,
  onModuleClear,
  showModules = true,
}: SegmentedFilterButtonsProps) {
  const [activeSegment, setActiveSegment] = useState<Segment>(Segment.Overall)
  const [backgroundStyle, setBackgroundStyle] = useState({
    left: 0,
    width: 0,
    top: 0,
    height: 0,
  })

  const isMobile = useMediaQuery("only screen and (max-width : 640px)")

  const containerRef = useRef<HTMLDivElement>(null)
  const overallRef = useRef<HTMLButtonElement>(null)
  const termsRef = useRef<HTMLButtonElement>(null)
  const modulesRef = useRef<HTMLButtonElement>(null)

  const containerPadding = 3
  const backgroundGapX = 4
  const backgroundGapY = 4

  useEffect(() => {
    if (selectedModule.value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveSegment(Segment.Modules)
    } else if (selectedTerm.value) {
      setActiveSegment(Segment.Terms)
    } else {
      setActiveSegment(Segment.Overall)
    }
  }, [selectedTerm.value, selectedModule.value])

  useEffect(() => {
    const updateBackgroundPosition = () => {
      if (!containerRef.current) return

      let activeRef: React.RefObject<HTMLButtonElement>
      switch (activeSegment) {
        case Segment.Overall:
          activeRef = overallRef
          break
        case Segment.Terms:
          activeRef = termsRef
          break
        case Segment.Modules:
          activeRef = modulesRef
          break
        default:
          activeRef = overallRef
      }

      if (activeRef.current && containerRef.current) {
        const container = containerRef.current
        const activeButton = activeRef.current
        const containerRect = container.getBoundingClientRect()
        const buttonRect = activeButton.getBoundingClientRect()

        if (isMobile) {
          // Vertical layout for mobile
          const top =
            buttonRect.top -
            containerRect.top -
            containerPadding +
            backgroundGapY
          const height = buttonRect.height - backgroundGapY * 2
          const left = backgroundGapX
          const width =
            containerRect.width - containerPadding * 2 - backgroundGapX * 2

          setBackgroundStyle({ left, width, top, height })
        } else {
          // Horizontal layout for desktop
          const left =
            buttonRect.left -
            containerRect.left -
            containerPadding +
            backgroundGapX
          const width = buttonRect.width - backgroundGapX * 2
          const top = backgroundGapY
          const height =
            containerRect.height - containerPadding * 2 - backgroundGapY * 2

          setBackgroundStyle({ left, width, top, height })
        }
      }
    }

    const timer = setTimeout(updateBackgroundPosition, 10)
    window.addEventListener("resize", updateBackgroundPosition)

    return () => {
      clearTimeout(timer)
      window.removeEventListener("resize", updateBackgroundPosition)
    }
  }, [
    activeSegment,
    selectedTerm.label,
    selectedModule.label,
    showModules,
    backgroundGapX,
    backgroundGapY,
    isMobile,
  ])

  return (
    <div
      ref={containerRef}
      className="relative inline-flex w-full flex-col items-center justify-center rounded-xl bg-gray-100 p-0.75 sm:w-auto sm:flex-row"
    >
      <div
        className="absolute rounded-xl bg-white shadow-md transition-all duration-200 ease-out"
        style={{
          left: `${backgroundStyle.left + containerPadding}px`,
          width: `${backgroundStyle.width}px`,
          top: `${backgroundStyle.top + containerPadding}px`,
          height: `${backgroundStyle.height}px`,
        }}
      />

      <button
        ref={overallRef}
        onClick={() => {
          onOverallClick()
          setActiveSegment(Segment.Overall)
        }}
        className={clsx(
          "relative z-10 flex w-full cursor-pointer items-center whitespace-nowrap rounded-xl px-3 py-1.5 text-sm font-medium transition-colors duration-200 sm:w-auto sm:justify-center",
          {
            "text-gray-900": activeSegment === Segment.Overall,
            "text-gray-600 hover:text-gray-900":
              activeSegment !== Segment.Overall,
          }
        )}
      >
        Overall
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            ref={termsRef}
            onClick={() => setActiveSegment(Segment.Terms)}
            className={clsx(
              "relative z-10 flex w-full cursor-pointer items-center justify-between gap-1 whitespace-nowrap rounded-xl px-3 py-1.5 text-sm font-medium transition-colors duration-200 sm:w-auto sm:justify-center",
              {
                "text-gray-900": activeSegment === Segment.Terms,
                "text-gray-600 hover:text-gray-900":
                  activeSegment !== Segment.Terms,
              }
            )}
          >
            <span>
              {selectedTerm.value ? `Term ${selectedTerm.value}` : "Terms"}
            </span>
            {selectedTerm.value ? (
              <XIcon
                onClick={e => {
                  e.stopPropagation()
                  onTermClear()
                }}
                className="ml-1 !size-2.25 shrink-0 text-gray-500 hover:text-gray-900"
              />
            ) : (
              <ChevronDown className="ml-1 !size-2.25 shrink-0" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[90vw] sm:w-[140px]">
          {termOptions.map(option => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => {
                if (option.value && !option.isDisabled) {
                  onTermSelect(option)
                  setActiveSegment(Segment.Terms)
                }
              }}
              className={clsx("text-onSurface-600", {
                "!text-onSurface-300 !cursor-default": option.isDisabled,
              })}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {showModules && moduleOptions && moduleOptions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              ref={modulesRef}
              onClick={() => setActiveSegment(Segment.Modules)}
              className={clsx(
                "relative z-10 flex w-full cursor-pointer items-center justify-between gap-1 whitespace-nowrap rounded-xl px-3 py-1.5 text-sm font-medium transition-colors duration-200 sm:w-auto sm:justify-center",
                {
                  "text-gray-900": activeSegment === Segment.Modules,
                  "text-gray-600 hover:text-gray-900":
                    activeSegment !== Segment.Modules,
                }
              )}
            >
              <span className="truncate">
                {selectedModule.label || "Modules"}
              </span>
              {selectedModule.value ? (
                <XIcon
                  onClick={e => {
                    e.stopPropagation()
                    onModuleClear()
                  }}
                  className="ml-1 !size-2.25 shrink-0 text-gray-500 hover:text-gray-900"
                />
              ) : (
                <ChevronDown className="ml-1 !size-2.25 shrink-0" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[90vw] sm:w-[250px]">
            {[...moduleOptions]
              .sort((a, b) => {
                if (a.isDisabled === b.isDisabled) return 0
                return a.isDisabled ? 1 : -1
              })
              .map(option => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => {
                    if (option.value && !option.isDisabled) {
                      onModuleSelect(option)
                      setActiveSegment(Segment.Modules)
                    }
                  }}
                  className={clsx("text-onSurface-600", {
                    "!text-onSurface-300 !cursor-default": option.isDisabled,
                  })}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
